// src/features/project/EpicsPanel.tsx
// Epics: ListEpics + Create / Get / Update / Delete, with progress. Linking items to
// an epic (AddItemToEpic / RemoveItemFromEpic) happens from the item rail.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useProjectEpic } from '@react/hooks';
import { Button, Input, Textarea, Card, CardContent, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ProjectModal, Field, ProgressBar } from './ProjectModal';
import { epicStatusMap, epicStatusOptions, idOf, listParams, toTimestamp, toDateInputValue } from './meta';
import { getStatusMapping } from './logic';
import type { Entity } from '@core/types/entity';
import type { Epic } from '@core/types/project';

export const EpicsPanel = observer(({ projectEntity, onChanged }: { projectEntity: Entity; onChanged: () => void }) => {
  const { t } = useI18n();
  const store = useProjectEpic();
  const projectId = projectEntity.id;

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Epic | null>(null);
  const [detail, setDetail] = useState<Epic | null>(null);
  const [deleting, setDeleting] = useState<Epic | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = () => store.ListEpics(projectId, listParams());
  useEffect(() => {
    reload();
  }, [projectId]);

  const epics: Epic[] = store.items ?? [];

  const del = () =>
    deleting &&
    (async () => {
      setBusy(true);
      try {
        await store.DeleteEpic(projectId, idOf(deleting));
        setDeleting(null);
        reload();
        onChanged();
      } catch {
        /* store surfaces error */
      } finally {
        setBusy(false);
      }
    })();

  const openDetail = async (epic: Epic) => {
    setDetail(epic);
    try {
      const full = await store.GetEpic(projectId, idOf(epic));
      setDetail(full);
    } catch {
      /* keep the list version */
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('project.epics.title')}</h2>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          {t('project.epics.create')}
        </Button>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {epics.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-lg font-medium text-foreground">{t('project.epics.empty')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('project.epics.emptyDesc')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {epics.map((epic) => {
            const status = getStatusMapping(epicStatusMap, epic.status);
            return (
              <Card key={idOf(epic)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => openDetail(epic)}
                      className="flex min-w-0 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {epic.color && (
                        <span className="h-3 w-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: epic.color }} aria-hidden="true" />
                      )}
                      <span className="truncate text-sm font-semibold text-foreground">{epic.name}</span>
                    </button>
                    <Badge variant={status.variant as never} size="sm">
                      {t(status.labelKey)}
                    </Badge>
                  </div>
                  {epic.description && (
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{epic.description}</p>
                  )}
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1">
                      <ProgressBar percent={epic.progress_percent ?? 0} />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {epic.completed_items ?? 0}/{epic.total_items ?? 0}
                    </span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setEditing(epic);
                        setShowForm(true);
                      }}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => setDeleting(epic)}>
                      {t('common.delete')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showForm && (
        <EpicFormDialog
          projectEntity={projectEntity}
          epic={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            reload();
            onChanged();
          }}
        />
      )}

      {detail && <EpicDetailDialog epic={detail} onClose={() => setDetail(null)} />}

      <ConfirmDialog
        open={!!deleting}
        title={t('project.epics.deleteTitle')}
        description={t('project.epics.deleteDesc')}
        destructive
        isConfirming={busy}
        onConfirm={del}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
});

EpicsPanel.displayName = 'EpicsPanel';

function EpicFormDialog({
  projectEntity,
  epic,
  onClose,
  onSaved,
}: {
  projectEntity: Entity;
  epic: Epic | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const store = useProjectEpic();
  const [name, setName] = useState(epic?.name ?? '');
  const [description, setDescription] = useState(epic?.description ?? '');
  const [color, setColor] = useState(epic?.color ?? '#6366f1');
  const [status, setStatus] = useState(epic?.status ?? 'EPIC_STATUS_OPEN');
  const [startDate, setStartDate] = useState(toDateInputValue(epic?.start_date));
  const [targetDate, setTargetDate] = useState(toDateInputValue(epic?.target_date));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (epic) {
        await store.UpdateEpic(projectEntity.id, idOf(epic), {
          project: projectEntity,
          epic: {
            ...epic,
            name: name.trim(),
            description,
            color,
            status: status as never,
            start_date: toTimestamp(startDate),
            target_date: toTimestamp(targetDate),
          },
          field_mask: { paths: ['name', 'description', 'color', 'status', 'start_date', 'target_date'] },
        } as never);
      } else {
        await store.CreateEpic(projectEntity.id, {
          project: projectEntity,
          name: name.trim(),
          description: description || undefined,
          color,
        } as never);
      }
      onSaved();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProjectModal
      open
      title={epic ? t('project.epics.editTitle') : t('project.epics.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !name.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!!store.error && <ErrorAlert error={store.error} />}
        <Field label={t('project.epics.name')} htmlFor="epic-name">
          <Input id="epic-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('project.epics.description')} htmlFor="epic-desc">
          <Textarea id="epic-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('project.epics.color')} htmlFor="epic-color">
            <input
              id="epic-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 w-full cursor-pointer rounded-md border border-input bg-background"
            />
          </Field>
          {epic && (
            <Field label={t('project.epics.status')} htmlFor="epic-status">
              <Select id="epic-status" value={status} onChange={(e) => setStatus(e.target.value as never)} aria-label={t('project.epics.status')}>
                {epicStatusOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('project.epics.startDate')} htmlFor="epic-start">
            <Input id="epic-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label={t('project.epics.targetDate')} htmlFor="epic-target">
            <Input id="epic-target" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
          </Field>
        </div>
      </div>
    </ProjectModal>
  );
}

function EpicDetailDialog({ epic, onClose }: { epic: Epic; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <ProjectModal open title={epic.name} onClose={onClose}>
      <div className="space-y-4">
        {epic.description && <p className="text-sm text-muted-foreground">{epic.description}</p>}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Stat label={t('project.epics.items')} value={`${epic.completed_items ?? 0}/${epic.total_items ?? 0}`} />
          <Stat label={t('project.epics.points')} value={`${epic.completed_points ?? 0}/${epic.total_points ?? 0}`} />
        </div>
        <ProgressBar percent={epic.progress_percent ?? 0} />
      </div>
    </ProjectModal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}
